import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ordersApi } from "@/services/api";
import type { WorkOrderDto } from "@/services/api";
import { RefreshCw, Package, Plus, GripVertical, Eye, Layers, Box, Boxes, LayoutList } from "lucide-react";
import { LoadingPlanResponse, PACKING_ALGORITHMS } from "@/types/loading-plan";
import { Pallet3DViewer } from "@/components/loading-plan/Pallet3DViewer";

interface OrderGroupsSidebarProps {
  loadingPlan: LoadingPlanResponse;
  onApplyGroups: (groups: string[][], algorithms: (string | null)[]) => Promise<void>;
  disabled?: boolean;
}

export function OrderGroupsSidebar({
  loadingPlan,
  onApplyGroups,
  disabled,
}: OrderGroupsSidebarProps) {
  const [orders, setOrders] = useState<WorkOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [draggedOrderId, setDraggedOrderId] = useState<string | null>(null);
  const [localGroups, setLocalGroups] = useState<string[][]>([]);
  const [localAlgorithms, setLocalAlgorithms] = useState<(string | null)[]>([]);
  /** Algoritmo por pedido cuando NO está en ningún grupo explícito. */
  const [soloAlgorithms, setSoloAlgorithms] = useState<Record<string, string | null>>({});
  const [viewOrder, setViewOrder] = useState<WorkOrderDto | null>(null);
  const [view3DPallet, setView3DPallet] = useState<{
    id: string;
    type: "virtual" | "physical";
    number: number | string;
  } | null>(null);

  // ── Estado para el visor de grupo ──
  const [viewGroupIdx, setViewGroupIdx] = useState<number | null>(null);
  const [viewGroupPallet, setViewGroupPallet] = useState<{
    id: string;
    type: "virtual" | "physical";
    number: number | string;
  } | null>(null);
  const [groupOrderFilter, setGroupOrderFilter] = useState<string | null>(null);

  // Auto-seleccionar primer palet del grupo al abrirlo
  useEffect(() => {
    if (viewGroupIdx === null) { setViewGroupPallet(null); setGroupOrderFilter(null); return; }
    const ids = localGroups[viewGroupIdx] ?? [];
    const firstV = (loadingPlan.virtualPallets ?? []).find(p => p.workOrderIds?.some(id => ids.includes(id)));
    if (firstV) { setViewGroupPallet({ id: firstV.id, type: "virtual", number: firstV.sequenceNumber }); return; }
    const firstP = (loadingPlan.physicalPallets ?? []).find(p => p.workOrderIds?.some(id => ids.includes(id)));
    setViewGroupPallet(firstP ? { id: firstP.id, type: "physical", number: firstP.palletCode } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewGroupIdx]);

  // Auto-seleccionar el primer palet cuando se abre el detalle de un pedido
  useEffect(() => {
    if (!viewOrder) { setView3DPallet(null); return; }
    const { virtual, physical } = getPalletsForOrder(viewOrder.id);
    if (virtual.length > 0) {
      setView3DPallet({ id: virtual[0].id, type: "virtual", number: virtual[0].sequenceNumber });
    } else if (physical.length > 0) {
      setView3DPallet({ id: physical[0].id, type: "physical", number: physical[0].palletCode });
    } else {
      setView3DPallet(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOrder?.id]);

  const getPalletsForOrder = (orderId: string) => {
    const virtual = (loadingPlan.virtualPallets ?? []).filter((p) =>
      p.workOrderIds?.includes(orderId),
    );
    const physical = (loadingPlan.physicalPallets ?? []).filter((p) =>
      p.workOrderIds?.includes(orderId),
    );
    return { virtual, physical };
  };

  useEffect(() => {
    const groups = loadingPlan.orderGroups ?? [];
    const algorithms = loadingPlan.groupAlgorithms ?? [];

    const multiGroups: string[][] = [];
    const multiAlgorithms: (string | null)[] = [];
    const newSoloAlgorithms: Record<string, string | null> = {};

    groups.forEach((group, idx) => {
      const algo = algorithms[idx] ?? null;
      if (group.length > 1) {
        multiGroups.push(group);
        multiAlgorithms.push(algo);
      } else if (group.length === 1 && algo) {
        // Solo group con algoritmo explícito → guardar en soloAlgorithms
        newSoloAlgorithms[group[0]] = algo;
      }
      // Solo group sin algoritmo → se deja como pedido suelto (backend lo recoge implícitamente)
    });

    setLocalGroups(multiGroups);
    setLocalAlgorithms(multiAlgorithms);
    setSoloAlgorithms(newSoloAlgorithms);
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingPlan.id,
    loadingPlan.plannedDate,
    loadingPlan.routeId,
    loadingPlan.orderGroups,
    loadingPlan.groupAlgorithms,
  ]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch orders for the specific date
      const data = await ordersApi.getAll({
        dateFrom: loadingPlan.plannedDate.split("T")[0],
        dateTo: loadingPlan.plannedDate.split("T")[0],
      });
      // Filter by route
      const routeOrders = data.filter(
        (o) => o.routeId === loadingPlan.routeId && o.status !== "Completed",
      );
      setOrders(routeOrders);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  const isOrderInAnyGroup = (orderId: string) => {
    return localGroups.some((group) => group.includes(orderId));
  };

  const handleAddGroup = () => {
    setLocalGroups((prev) => [[], ...prev]);
    setLocalAlgorithms((prev) => [null, ...prev]);
  };

  const handleDeleteGroup = (index: number) => {
    setLocalGroups((prev) => prev.filter((_, i) => i !== index));
    setLocalAlgorithms((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSetAlgorithm = (groupIndex: number, algorithm: string | null) => {
    setLocalAlgorithms((prev) => {
      const next = [...prev];
      next[groupIndex] = algorithm;
      return next;
    });
  };

  const handleSetSoloAlgorithm = (orderId: string, algorithm: string | null) => {
    setSoloAlgorithms((prev) => ({ ...prev, [orderId]: algorithm }));
  };

  const handleApply = () => {
    const allGroups = [...localGroups];
    const allAlgorithms = [...localAlgorithms];
    // Pedidos sin grupo → grupo de 1 con su algoritmo individual
    orders
      .filter((o) => !isOrderInAnyGroup(o.id))
      .forEach((o) => {
        allGroups.push([o.id]);
        allAlgorithms.push(soloAlgorithms[o.id] ?? null);
      });
    onApplyGroups(allGroups, allAlgorithms);
  };

  const handleRemoveFromGroup = (groupIndex: number, orderId: string) => {
    setLocalGroups((prev) => {
      const newGroups = [...prev];
      newGroups[groupIndex] = newGroups[groupIndex].filter(
        (id) => id !== orderId,
      );
      return newGroups;
    });
  };

  const handleDragStart = (e: React.DragEvent, orderId: string) => {
    e.dataTransfer.setData("orderId", orderId);
    setDraggedOrderId(orderId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, groupIndex: number) => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData("orderId");
    if (orderId && !isOrderInAnyGroup(orderId)) {
      setLocalGroups((prev) => {
        const newGroups = [...prev];
        newGroups[groupIndex] = [...newGroups[groupIndex], orderId];
        return newGroups;
      });
    }
    setDraggedOrderId(null);
  };

  const handleDragEnd = () => {
    setDraggedOrderId(null);
  };

  return (
    <>
    <Card className="flex flex-col h-full rounded-none border-0 shadow-none bg-transparent">
      <CardHeader className="pb-3 border-b shrink-0">
        <CardTitle className="flex justify-between items-center text-base">
          <Badge variant="secondary">{localGroups.length} grupos</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto space-y-6 pt-6 pb-4 min-h-0">
        {loading ? (
          <div className="flex justify-center p-4">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-red-500 text-sm">{error}</div>
        ) : (
          <>
            {/* Action Bar */}
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Grupos
              </h3>
              <Button
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={handleAddGroup}
              >
                <Plus className="w-4 h-4 mr-1" />
                Añadir Grupo
              </Button>
            </div>

            {/* Configured Groups */}
            <div className="space-y-3">
              {localGroups.map((group, idx) => (
                <div
                  key={idx}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, idx)}
                  className={`p-3 rounded-lg border-2 border-dashed transition-all relative ${
                    draggedOrderId
                      ? "border-primary bg-primary/5 shadow-sm"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-primary flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      Grupo {localGroups.length - idx}
                    </span>
                    <div className="flex items-center gap-0.5">
                      {group.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                          onClick={() => setViewGroupIdx(idx)}
                          title="Ver resumen del grupo"
                        >
                          <LayoutList className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                        onClick={() => handleDeleteGroup(idx)}
                        disabled={disabled}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                  {/* Selector de algoritmo */}
                  <div className="mb-2">
                    <Select
                      disabled={disabled}
                      value={localAlgorithms[idx] ?? "ShelfNextFit"}
                      onValueChange={(val) => handleSetAlgorithm(idx, val === "ShelfNextFit" ? null : val)}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Algoritmo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PACKING_ALGORITHMS.map((algo) => (
                          <SelectItem key={algo.key} value={algo.key} className="text-xs">
                            <span className="font-medium">{algo.label}</span>
                            <span className="text-muted-foreground ml-1 hidden sm:inline">— {algo.description}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 min-h-10 flex flex-col justify-center">
                    {group.length === 0 ? (
                      <div className="text-xs text-muted-foreground text-center italic py-2 cursor-default">
                        Grupo vacío (arrastra pedidos aquí)
                      </div>
                    ) : (
                      group.map((orderId) => {
                        const order = orders.find((o) => o.id === orderId);
                        return (
                          <div
                            key={orderId}
                            className="text-xs bg-background border px-2 py-1.5 rounded flex justify-between items-center shadow-sm gap-1"
                          >
                            <span className="font-medium flex-1 truncate">
                              {order?.customerName || orderId.substring(0, 8)}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                              onClick={() => order && setViewOrder(order)}
                              title="Ver detalle del pedido"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-red-500"
                              onClick={() =>
                                handleRemoveFromGroup(idx, orderId)
                              }
                              disabled={disabled}
                            >
                              ✕
                            </Button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Ungrouped Orders List */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold mb-3 flex items-center text-muted-foreground">
                Pedidos Disponibles (arrastrar)
              </h3>
              <div className="space-y-2">
                {orders
                  .filter((o) => !isOrderInAnyGroup(o.id))
                  .map((order) => (
                    <div
                      key={order.id}
                      draggable={!disabled}
                      onDragStart={(e) => handleDragStart(e, order.id)}
                      onDragEnd={handleDragEnd}
                      className={`rounded-lg border transition-all ${
                        draggedOrderId === order.id
                          ? "opacity-50 border-primary scale-[0.98]"
                          : "bg-card hover:bg-accent hover:border-primary/30"
                      }`}
                    >
                      {/* Fila principal */}
                      <div className="flex items-center gap-2 px-2 pt-2 pb-1">
                        <GripVertical className="hidden sm:block text-muted-foreground/50 w-4 h-4 shrink-0 cursor-grab active:cursor-grabbing" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {order.customerName || "Sin cliente"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {order.lines?.length || 0} líneas
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-primary shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewOrder(order);
                          }}
                          title="Ver detalle del pedido"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Selector de algoritmo individual */}
                      <div className="px-2 pb-2">
                        <Select
                          disabled={disabled}
                          value={soloAlgorithms[order.id] ?? "ShelfNextFit"}
                          onValueChange={(val) =>
                            handleSetSoloAlgorithm(order.id, val === "ShelfNextFit" ? null : val)
                          }
                        >
                          <SelectTrigger
                            className="h-6 text-[11px] px-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue placeholder="Algoritmo..." />
                          </SelectTrigger>
                          <SelectContent>
                            {PACKING_ALGORITHMS.map((algo) => (
                              <SelectItem key={algo.key} value={algo.key} className="text-xs">
                                {algo.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                {orders.filter((o) => !isOrderInAnyGroup(o.id)).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center p-4 border rounded-lg bg-muted/10 border-dashed">
                    No hay pedidos disponibles
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
      {/* Apply Button at bottom */}
      <div className="p-4 border-t bg-card shrink-0">
        <Button
          className="w-full"
          disabled={disabled}
          onClick={handleApply}
        >
          Aplicar y Recalcular
        </Button>
      </div>
    </Card>

    {/* ── Group view dialog ── */}
    {viewGroupIdx !== null && (() => {
      const groupOrderIds = localGroups[viewGroupIdx] ?? [];
      const groupOrders = orders.filter(o => groupOrderIds.includes(o.id));
      const groupVirtual = (loadingPlan.virtualPallets ?? []).filter(p =>
        p.workOrderIds?.some(id => groupOrderIds.includes(id))
      );
      const groupPhysical = (loadingPlan.physicalPallets ?? []).filter(p =>
        p.workOrderIds?.some(id => groupOrderIds.includes(id))
      );
      const totalGroupPallets = groupVirtual.length + groupPhysical.length;

      // Si hay filtro de pedido, resaltar palets que lo contienen
      const palletHasFilter = (ids?: string[]) =>
        !groupOrderFilter || (ids?.includes(groupOrderFilter) ?? false);

      return (
        <Dialog open onOpenChange={() => setViewGroupIdx(null)}>
          <DialogContent className="max-w-lg lg:max-w-5xl max-h-[85vh] lg:h-[85vh] flex flex-col lg:flex-row p-0 gap-0 overflow-hidden">
            {/* Panel izquierdo */}
            <div className="flex flex-col w-full lg:w-96 shrink-0 border-r overflow-y-auto">
              <div className="p-5 border-b shrink-0">
                <DialogTitle className="flex items-center gap-2 text-base">
                  <LayoutList className="w-4 h-4 text-primary" />
                  Grupo {localGroups.length - viewGroupIdx}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {groupOrders.length} pedido{groupOrders.length !== 1 ? "s" : ""}
                  {" · "}
                  {totalGroupPallets} palet{totalGroupPallets !== 1 ? "s" : ""}
                  {groupOrderFilter && (
                    <button
                      className="ml-2 underline text-primary hover:no-underline"
                      onClick={() => setGroupOrderFilter(null)}
                    >
                      Quitar filtro
                    </button>
                  )}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {/* Pedidos */}
                <section>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Pedidos del grupo
                  </h4>
                  <div className="space-y-1.5">
                    {groupOrders.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Sin pedidos cargados.</p>
                    )}
                    {groupOrders.map(order => {
                      const { virtual: ov, physical: op } = getPalletsForOrder(order.id);
                      const isFiltered = groupOrderFilter === order.id;
                      return (
                        <button
                          key={order.id}
                          className={`w-full text-left p-2 rounded-lg border text-xs transition-all ${
                            isFiltered
                              ? "border-primary bg-primary/5 ring-1 ring-primary"
                              : "border-border hover:border-primary/40 hover:bg-muted/40"
                          }`}
                          onClick={() => setGroupOrderFilter(isFiltered ? null : order.id)}
                          title={isFiltered ? "Quitar filtro" : "Filtrar palets por este pedido"}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold truncate">{order.customerName || "Sin cliente"}</span>
                            <span className="text-muted-foreground shrink-0">{order.lines?.length ?? 0} líneas</span>
                          </div>
                          {(ov.length + op.length) > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {ov.map(p => (
                                <span key={p.id} className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                  Virt. #{p.sequenceNumber}
                                </span>
                              ))}
                              {op.map(p => (
                                <span key={p.id} className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                  {p.palletCode}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* Palets */}
                <section>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Box className="w-3 h-3" /> Palets generados
                    {totalGroupPallets === 0 && (
                      <span className="normal-case font-normal ml-1">(recalcula para generar)</span>
                    )}
                  </h4>
                  <div className="space-y-1.5">
                    {groupVirtual.map(p => {
                      const ordersInPallet = groupOrders.filter(o => p.workOrderIds?.includes(o.id));
                      const dimmed = groupOrderFilter && !p.workOrderIds?.includes(groupOrderFilter);
                      const selected = viewGroupPallet?.id === p.id;
                      return (
                        <div
                          key={p.id}
                          className={`p-2 rounded-lg border cursor-pointer transition-all ${
                            selected
                              ? "border-primary ring-1 ring-primary bg-primary/5"
                              : dimmed
                              ? "border-border opacity-35"
                              : "border-blue-200 bg-blue-50 dark:bg-blue-950 hover:border-blue-400"
                          }`}
                          onClick={() => setViewGroupPallet({ id: p.id, type: "virtual", number: p.sequenceNumber })}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                              Palet Virtual #{p.sequenceNumber}
                            </span>
                            <span className="text-[10px] text-muted-foreground">{p.totalBoxes} cajas · {p.totalWeightKg.toFixed(1)} kg</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {ordersInPallet.map(o => (
                              <span
                                key={o.id}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  groupOrderFilter === o.id
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                }`}
                              >
                                {o.customerName || o.id.substring(0, 6)}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {groupPhysical.map(p => {
                      const ordersInPallet = groupOrders.filter(o => p.workOrderIds?.includes(o.id));
                      const dimmed = groupOrderFilter && !p.workOrderIds?.includes(groupOrderFilter);
                      const selected = viewGroupPallet?.id === p.id;
                      return (
                        <div
                          key={p.id}
                          className={`p-2 rounded-lg border cursor-pointer transition-all ${
                            selected
                              ? "border-primary ring-1 ring-primary bg-primary/5"
                              : dimmed
                              ? "border-border opacity-35"
                              : "border-green-200 bg-green-50 dark:bg-green-950 hover:border-green-400"
                          }`}
                          onClick={() => setViewGroupPallet({ id: p.id, type: "physical", number: p.palletCode })}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                              Palet {p.palletCode}
                            </span>
                            <span className="text-[10px] text-muted-foreground">{p.totalBoxes} cajas · {p.actualWeightKg.toFixed(1)} kg</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {ordersInPallet.map(o => (
                              <span
                                key={o.id}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  groupOrderFilter === o.id
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                }`}
                              >
                                {o.customerName || o.id.substring(0, 6)}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            </div>

            {/* Panel derecho: visor 3D embebido */}
            <div className="hidden lg:flex flex-1 overflow-hidden">
              <Pallet3DViewer
                embedded
                palletId={viewGroupPallet?.id ?? null}
                type={viewGroupPallet?.type ?? "virtual"}
                isOpen={true}
                onClose={() => setViewGroupPallet(null)}
                palletNumber={viewGroupPallet?.number ?? ""}
              />
            </div>
          </DialogContent>
        </Dialog>
      );
    })()}

    {/* Order detail dialog */}
    <Dialog open={!!viewOrder} onOpenChange={(open) => { if (!open) { setViewOrder(null); setView3DPallet(null); } }}>
      <DialogContent className="max-w-lg lg:max-w-5xl max-h-[85vh] lg:h-[85vh] flex flex-col lg:flex-row p-0 gap-0 overflow-hidden">
        {/* Panel izquierdo: detalle del pedido */}
        <div className="flex flex-col w-full lg:w-96 shrink-0 overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {viewOrder?.customerName || "Sin cliente"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {viewOrder?.orderDate
              ? new Date(viewOrder.orderDate).toLocaleDateString()
              : "Sin fecha"}
            {" · "}
            <span className="capitalize">{viewOrder?.status ?? ""}</span>
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          {/* Lines */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
              <Layers className="w-4 h-4" />
              Líneas del pedido
            </h4>
            {viewOrder?.lines && viewOrder.lines.length > 0 ? (
              <div className="space-y-1.5">
                {viewOrder.lines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-center justify-between p-2 rounded border bg-muted/30 text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate block">
                        {line.productName || "Producto desconocido"}
                      </span>
                      {line.packagingContainerName && (
                        <span className="text-xs text-muted-foreground">
                          {line.packagingContainerName}
                        </span>
                      )}
                    </div>
                    <Badge variant="secondary" className="ml-2 shrink-0">
                      {line.quantity} {line.unit || "uds"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">Sin líneas</p>
            )}
          </div>

          {/* Pallets */}
          {viewOrder && (() => {
            const { virtual, physical } = getPalletsForOrder(viewOrder.id);
            const totalPallets = virtual.length + physical.length;
            return totalPallets > 0 ? (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Box className="w-4 h-4" />
                  Palets generados ({totalPallets})
                </h4>
                <div className="space-y-1.5">
                  {virtual.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between p-2 rounded border bg-blue-50 dark:bg-blue-950 text-sm"
                    >
                      <span className="font-medium text-blue-700 dark:text-blue-300">
                        Palet Virtual #{p.sequenceNumber}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{p.totalBoxes} cajas</span>
                        <span>{p.totalWeightKg.toFixed(1)} kg</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 shrink-0"
                          title="Ver distribución 3D"
                          onClick={() =>
                            setView3DPallet({
                              id: p.id,
                              type: "virtual",
                              number: p.sequenceNumber,
                            })
                          }
                        >
                          <Boxes className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {physical.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between p-2 rounded border bg-green-50 dark:bg-green-950 text-sm"
                    >
                      <span className="font-medium text-green-700 dark:text-green-300">
                        Palet {p.palletCode}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{p.totalBoxes} cajas</span>
                        <span>{p.actualWeightKg.toFixed(1)} kg</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-green-600 hover:text-green-800 shrink-0"
                          title="Ver distribución 3D"
                          onClick={() =>
                            setView3DPallet({
                              id: p.id,
                              type: "physical",
                              number: p.palletCode,
                            })
                          }
                        >
                          <Boxes className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Box className="w-4 h-4" />
                  Palets generados
                </h4>
                <p className="text-sm text-muted-foreground italic">
                  Aún no se han generado palets para este pedido.
                </p>
              </div>
            );
          })()}
        </div>
        </div>{/* fin panel izquierdo */}

        {/* Panel derecho: visor 3D embebido (solo lg+) */}
        <div className="hidden lg:flex flex-1 border-l overflow-hidden">
          <Pallet3DViewer
            embedded
            palletId={view3DPallet?.id ?? null}
            type={view3DPallet?.type ?? "virtual"}
            isOpen={true}
            onClose={() => setView3DPallet(null)}
            palletNumber={view3DPallet?.number ?? ""}
          />
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
